'use client'

import React, { useCallback, useMemo, useState } from 'react'

import { Card, CardContent, CardHeader } from '@/components/ui/card'
import PhoneOtpSection from '@/components/booking/PhoneOtpSection'
import type { BookingData, BookingSetter } from '@/types/booking'

interface StepPhoneVerifyProps {
  useWhatsApp: boolean
  bookingData: BookingData
  setBookingData: BookingSetter
  onVerified: (phone: string) => void
}

const StepPhoneVerify: React.FC<StepPhoneVerifyProps> = ({
  useWhatsApp,
  bookingData,
  setBookingData,
  onVerified,
}) => {
  const [dirtyPrimary, setDirtyPrimary] = useState(false)

  const [otp, setOtp] = useState('')
  const [otpSent, setOtpSent] = useState(false)
  const [otpSending, setOtpSending] = useState(false)
  const [otpVerified, setOtpVerified] = useState(false)
  const [otpVerifying, setOtpVerifying] = useState(false)
  const [otpError, setOtpError] = useState<string | null>(null)
  const [otpChallenge, setOtpChallenge] = useState<string | null>(null)
  const [otpTimerKey, setOtpTimerKey] = useState(0)

  const phoneDigits = useMemo(
    () => (bookingData.phone || '').replace(/\D/g, '').slice(-10),
    [bookingData.phone],
  )

  const validPrimary = /^\d{10}$/.test(phoneDigits)

  const resetOtpState = useCallback(() => {
    setOtp('')
    setOtpSent(false)
    setOtpSending(false)
    setOtpVerified(false)
    setOtpVerifying(false)
    setOtpError(null)
    setOtpChallenge(null)
    setOtpTimerKey(0)
  }, [])

  const handlePhoneChange = useCallback(
    (digits: string) => {
      const normalized = digits.replace(/\D/g, '').slice(-10)

      if (normalized !== phoneDigits) {
        resetOtpState()
      }

      setBookingData((prev) => ({
        ...prev,
        phone: normalized,
        phoneVerified: false,
        existingPatientId: null,
        existingPatientHumanId: null,
        preferredPsychologist: null,
        patientMode: null,
        name: '',
        dob: undefined,
        gender: '',
        email: '',
        flowType: null,
        assessmentType: null,
        doctorID: '',
        doctorName: '',
        doctorSpecialty: '',
        bookingDate: undefined,
        slot: '',
      }))
    },
    [phoneDigits, resetOtpState, setBookingData],
  )

  const handleSendOtp = useCallback(async () => {
    if (!validPrimary) return

    setOtpError(null)
    setOtp('')
    setOtpSending(true)

    setBookingData((prev) => ({
      ...prev,
      phone: phoneDigits,
      phoneVerified: false,
      existingPatientId: null,
      existingPatientHumanId: null,
      preferredPsychologist: null,
      patientMode: null,
    }))

    try {
      const response = await fetch('/api/auth/otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mode: 'send',
          channel: useWhatsApp ? 'whatsapp' : 'sms',
          email: '',
          mobile: phoneDigits,
          doctorId: null,
          doctorName: null,
          userRole: null,
          specialty: null,
        }),
      })

      const data = (await response.json().catch(() => ({}))) as any

      if (!response.ok || !data?.ok) {
        throw new Error(
          data?.error || `Failed to send OTP (${response.status})`,
        )
      }

      setOtpChallenge(data.challenge ?? null)
      setOtpSent(true)
      setOtpTimerKey((key) => key + 1)
    } catch (err) {
      setOtpError(err instanceof Error ? err.message : 'Failed to send OTP.')
    } finally {
      setOtpSending(false)
    }
  }, [phoneDigits, setBookingData, useWhatsApp, validPrimary])

  const handleVerifyOtp = useCallback(async () => {
    if (!otp || otp.length < 6) return

    setOtpError(null)
    setOtpVerifying(true)

    try {
      const response = await fetch('/api/auth/otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mode: 'verify',
          email: '',
          otp,
          challenge: otpChallenge,
          mobile: phoneDigits,
        }),
      })

      const data = (await response.json().catch(() => ({}))) as any

      if (!response.ok || !data?.ok) {
        throw new Error(data?.error || 'Invalid OTP')
      }

      setOtpVerified(true)

      setBookingData((prev) => ({
        ...prev,
        phone: phoneDigits,
        phoneVerified: true,
      }))

      onVerified(phoneDigits)
    } catch (err) {
      setOtpError(
        err instanceof Error ? err.message : 'Invalid OTP. Please try again.',
      )

      setBookingData((prev) => ({
        ...prev,
        phoneVerified: false,
      }))
    } finally {
      setOtpVerifying(false)
    }
  }, [onVerified, otp, otpChallenge, phoneDigits, setBookingData])

  return (
    <div className="mx-auto flex w-full max-w-xl flex-col gap-5">
      <div className="text-center">
        <h2 className="text-xl font-semibold text-gray-900 sm:text-2xl">
          Verify your phone number
        </h2>
        <p className="mt-2 text-sm text-gray-600 sm:text-base">
          We’ll use this to securely find your ReACH profile before booking.
        </p>
      </div>

      <Card className="border shadow-lg">
        <CardHeader className="pb-0" />
        <CardContent className="px-4 pb-5 sm:px-6 sm:pb-6">
          <PhoneOtpSection
            useWhatsApp={useWhatsApp}
            phone={phoneDigits}
            onPhoneChange={handlePhoneChange}
            primaryLabel={useWhatsApp ? 'WhatsApp Number' : 'Phone Number'}
            primaryHelper={
              useWhatsApp
                ? "We'll send your OTP on WhatsApp."
                : "We'll send your OTP to this number."
            }
            dirtyPrimary={dirtyPrimary}
            setDirtyPrimary={setDirtyPrimary}
            validPrimary={validPrimary}
            otp={otp}
            setOtp={setOtp}
            otpSent={otpSent}
            otpSending={otpSending}
            otpVerified={otpVerified}
            otpVerifying={otpVerifying}
            otpError={otpError}
            onSendOtp={handleSendOtp}
            onVerifyOtp={handleVerifyOtp}
            showVerifiedMessage
            otpTimerKey={otpTimerKey}
          />
        </CardContent>
      </Card>
    </div>
  )
}

export default StepPhoneVerify
